import React, { useState, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import {
  reverse as _reverse,
  sortBy as _sortBy,
  filter as _filter,
  includes as _includes,
} from "lodash-es";

import Button from "../../components/Button";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalTitle,
} from "../../components/Modal";
import { useDebounce } from "../../utils";
import Avatar from "../../components/Avatar";
import Input from "../../components/Input";

const AvatarSelector = React.memo(({ human, onChange, checked }) => {
  const [isHumanSelected, setIsHumanSelected] = useState(checked);
  return (
    <div
      style={
        isHumanSelected
          ? { border: "2px solid rgb(67, 56, 202)", borderRadius: "10px" }
          : { padding: "2px" }
      }
      onClick={() => {
        setIsHumanSelected(!isHumanSelected);
        onChange(!isHumanSelected);
      }}
      className="cursor-pointer h-28 flex flex-col items-center w-24"
    >
      <Avatar
        className="mt-2"
        size="4rem"
        human={{ name: human.name, image: human.image }}
      />
      <p className="text-center w-full mt-2  truncate">{human.name}</p>
    </div>
  );
});

AvatarSelector.displayName = "AvatarSelector";
AvatarSelector.propTypes = {
  onChange: PropTypes.func.isRequired,
  human: PropTypes.any.isRequired,
  checked: PropTypes.bool.isRequired,
};

export default function AddMemeberModal({
  currentCircle,
  addHumans,
  humans,
  closeModal,
}) {
  const searchMemberRef = useRef(null);
  const [humanIds, setHumanIds] = useState([]);
  const [query, setQuery] = useState("");
  const queryDebounced = useDebounce(query, (value) => (value ? 100 : 0));

  const filteredHumans = useMemo(() => {
    const cleanQuery = queryDebounced.toLowerCase();
    if (cleanQuery.length < 2) {
      return [];
    }

    const filteredHumans = _filter(humans, (h) =>
      _includes(h.name.toLowerCase(), cleanQuery)
    );
    return _reverse(_sortBy(filteredHumans, "createdAt"));
  }, [humans, queryDebounced]);

  return (
    <Modal focusElement={searchMemberRef} isOpened={true} onClose={closeModal}>
      <ModalTitle>Add humans to {currentCircle.name}</ModalTitle>
      <ModalBody>
        <div className="mt-2 h-96 overflow-y-auto px-1">
          <Input
            className="my-2 mr-2 w-full"
            innerRef={searchMemberRef}
            type="text"
            placeholder="Search by name..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value.trimLeft());
            }}
            autoFocus
          />
          <div className="h-80 py-8 grid grid-cols-4 gap-1 overflow-x-auto">
            {filteredHumans
              .filter((human) =>
                currentCircle && currentCircle.humanIds
                  ? !currentCircle.humanIds.some((h) => human.id === h)
                  : human
              )
              .map((human, key) => {
                return (
                  <AvatarSelector
                    checked={humanIds.some((h) => h === human.id)}
                    onChange={(isChecked) => {
                      setHumanIds(
                        isChecked
                          ? [...humanIds, human.id]
                          : humanIds.filter((h) => h !== human.id)
                      );
                    }}
                    key={key}
                    human={human}
                  />
                );
              })}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          disabled={humanIds.length === 0}
          onClick={() => {
            addHumans(humanIds, currentCircle.id);
            closeModal();
          }}
        >
          Add {humanIds.length !== 0 ? humanIds.length : ""} Human
          {humanIds.length > 1 ? "s" : ""}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

AddMemeberModal.propTypes = {
  currentCircle: PropTypes.any.isRequired,
  addHumans: PropTypes.func.isRequired,
  humans: PropTypes.array.isRequired,
  closeModal: PropTypes.func.isRequired,
};
